import { YTTV_BASIC } from '~/constants/entityIds';
import { PLAN_SPEEDS_IDS, PLAN_SPEEDS_VRC_EVENT_NAME } from '~/constants/products';
import { DiscountOptions } from '~/types/common';
import { SelectedProductsState } from '~/types/products';
import checkVrcExperiencedEnabled, { CheckVrcExperiencedEnabled } from './checkVrcExperiencedEnabled';

const createInternetDiscount = (
  selectedState: SelectedProductsState,
  wasVrcModalShown: boolean,
  { vrcExperienceModalPlanSpeed, vrcExperienceModalEnabled }: CheckVrcExperiencedEnabled
): DiscountOptions => {
  const { internet } = selectedState || {};
  const { productId } = internet || {};

  // Verify if customer's selected plan matches what was offered in VRC modal
  const vrcExperienceProductId = PLAN_SPEEDS_IDS[vrcExperienceModalPlanSpeed];
  const isVrcProductSelected = Boolean(productId === vrcExperienceProductId);

  // Verify if the user selected the vrc after modal shown
  const isUserModalInteractionValid = vrcExperienceModalEnabled ? wasVrcModalShown : true;

  return {
    name: PLAN_SPEEDS_VRC_EVENT_NAME[vrcExperienceModalPlanSpeed],
    sku: vrcExperienceProductId,
    category: 'product',
    type: 'vrc',
    ftrTrackingKey: 'hasVrc',
    value: 100,
    active: isVrcProductSelected && isUserModalInteractionValid,
  };
};

const createYTTVDiscount = (
  selectedState: SelectedProductsState,
  { vrcExperienceYTTV: { value } }: CheckVrcExperiencedEnabled
): DiscountOptions | undefined => {
  const video = selectedState?.video || {};
  const { is1Gig, is2Gig, is5Gig, is7Gig } = selectedState?.internet;
  const isYTTVSelected = !!video[YTTV_BASIC];

  // If the number isn't valid, or between 1 - 300 return undefined
  if (isNaN(value) || typeof value !== 'number' || Math.min(Math.max(value, 1), 300) !== value) {
    return undefined;
  }

  return {
    name: 'yttv',
    sku: YTTV_BASIC,
    category: 'addOn',
    type: 'vrc',
    value: value,
    active: isYTTVSelected && (is1Gig || is2Gig || is5Gig || is7Gig),
  } as DiscountOptions;
};

export function createDiscountOptions({
  selectedState,
  wasVrcModalShown,
}: {
  selectedState: SelectedProductsState;
  wasVrcModalShown: boolean;
}): DiscountOptions[] {
  const vrcExperience = checkVrcExperiencedEnabled();
  const discounts: DiscountOptions[] = [];
  const internetDiscount = createInternetDiscount(selectedState, wasVrcModalShown, vrcExperience);
  discounts.push(internetDiscount);

  if (vrcExperience.vrcExperienceYTTV.enabled) {
    const yttvDiscount = createYTTVDiscount(selectedState, vrcExperience);
    if (yttvDiscount) {
      discounts.push(yttvDiscount);
    }
  }

  return discounts;
}

interface GenericInternetDiscountProps {
  amount: number;
  isActive: boolean;
  planSpeed: string;
  vrcName: string;
}

/**
 * Returns a discount object for an internet plan
 *
 * @param amount - number - The amount of the discount
 * @param isActive - boolean - Whether the discount is active
 * @param planSpeed - string - The plan speed, will be used as a key in PLAN_SPEEDS_IDS
 * @param vrcName - string - The name of the VRC
 * @returns
 */
export const createGenericInternetDiscount = ({
  amount,
  isActive,
  planSpeed,
  vrcName,
}: GenericInternetDiscountProps): DiscountOptions => {
  return {
    name: vrcName,
    sku: PLAN_SPEEDS_IDS[planSpeed],
    category: 'product',
    type: 'vrc',
    ftrTrackingKey: 'hasVrc',
    value: amount,
    active: isActive,
  };
};
